/**
 * Scroll animé vers les ancres internes (#parcours, etc.).
 *
 * ⚠ Ce fichier doit être chargé APRÈS navigation.js, qui crée le
 * ScrollSmoother (dépendance déclarée dans app/enqueue-scripts.php).
 *
 * Pourquoi pas un simple `window.scrollTo` animé : ScrollSmoother déplace le
 * contenu via un transform sur #smooth-content, la position de scroll native
 * (window.scrollY) et la position visible ne sont donc synchrones qu'à l'arrêt.
 * Écrire dans `window` — à la main ou via ScrollToPlugin — vise une cible
 * calculée sur une position faussée (on s'arrête un peu avant l'ancre) puis
 * laisse le smoother rattraper son retard pendant `smooth` secondes (1.8 s
 * ici), d'où le délai perçu.
 *
 * On passe donc par l'API du smoother :
 *   - `smoother.offset(el, "top <header>px")` calcule la position de scroll
 *     exacte de l'ancre via un ScrollTrigger temporaire — donc en tenant compte
 *     du pin de #site-header ;
 *   - `smoother.scrollTop` est un getter/setter qui déplace transform ET
 *     scrollbar dans le même frame : le tween arrive pile sur la cible, sans
 *     rattrapage derrière.
 */

k_animate_anchors_scroll();

function k_animate_anchors_scroll() {

  const DURATION = 0.8;
  const EASE = "power2.inOut";

  /**
   * Seuil au-delà duquel navigation.js masque le header au scroll vers le bas
   * (`currentScrollTop > lastScrollTop && currentScrollTop > 100`). Doit rester
   * aligné avec navigation.js, sinon l'offset de header se décale.
   */
  const HEADER_HIDE_THRESHOLD = 100;

  /** GSAP et ses plugins sont-ils chargés ? Sinon on laisse faire le natif. */
  function gsapReady() {
    return typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined";
  }

  function getSmoother() {
    return typeof ScrollSmoother !== "undefined" ? ScrollSmoother.get() : null;
  }

  /**
   * L'utilisateur a-t-il déjà pris la main sur le scroll ? Sert à ne pas le
   * ramener de force à l'ancre quand `load` arrive tardivement (ce thème sert
   * des visuels de plusieurs Mo : `load` peut tomber plusieurs secondes après
   * le premier rendu, alors que la lecture a commencé).
   */
  let userScrolled = false;

  ["wheel", "touchstart", "keydown", "mousedown"].forEach((type) => {
    window.addEventListener(type, () => { userScrolled = true; }, {
      passive: true,
      once: true,
    });
  });

  /**
   * Hauteur à déduire de la position cible : uniquement si #site-header
   * recouvre réellement l'ancre à l'arrivée, sinon l'offset creuse un blanc
   * au-dessus de la section.
   *
   * Deux cas :
   *  - header en `position: fixed` → il recouvre toujours (cas d'un thème sans
   *    ScrollSmoother) ;
   *  - avec ScrollSmoother, le header est épinglé par *transform* et non par
   *    `position: fixed` (le contenu étant lui-même transformé) : le test sur
   *    `position` renvoie donc toujours "static". Ce qui décide alors, c'est
   *    `.scrolling-hide` (navigation.js) : le header ne se masque QUE si on
   *    descend ET qu'on dépasse HEADER_HIDE_THRESHOLD. Dans tous les autres cas
   *    (remontée, chargement de page, cible haute) il reste visible.
   */
  function headerOffset(currentY, targetY, isLoad) {
    const header = document.querySelector("#site-header");

    if (!header) {
      return 0;
    }

    if (getComputedStyle(header).position === "fixed") {
      return header.offsetHeight;
    }

    const willHide =
      !isLoad && targetY > currentY && targetY > HEADER_HIDE_THRESHOLD;

    return willHide ? 0 : header.offsetHeight;
  }

  /**
   * Donne le focus clavier à l'ancre une fois arrivé — sans ça un lien
   * d'évitement (`.skip-link` vers #site-main) déplace la page mais pas le
   * focus, et la tabulation suivante repart dans le header.
   *
   * ⚠ À ne faire qu'à l'ARRIVÉE : ScrollSmoother écoute `focusin` et saute
   * instantanément en "center center" sur un élément focus hors viewport — ce
   * qui tuerait l'animation en cours.
   */
  function focusTarget(target) {
    if (!target.hasAttribute("tabindex")) {
      target.setAttribute("tabindex", "-1");
    }

    target.focus({ preventScroll: true });
  }

  function scrollTo(target, animate) {
    const smoother = getSmoother();
    const isLoad = !animate;

    // Pas de smoother (plugin absent, création échouée) : fallback ScrollTo.
    if (!smoother) {
      const currentY = window.scrollY;
      const targetY = target.getBoundingClientRect().top + currentY;

      gsap.to(window, {
        duration: animate ? DURATION : 0,
        scrollTo: {
          y: target,
          offsetY: headerOffset(currentY, targetY, isLoad),
        },
        overwrite: "auto",
        onComplete: () => focusTarget(target),
      });
      return;
    }

    // L'offset de header dépend du sens du scroll, qu'on ne connaît qu'une fois
    // la position brute de l'ancre calculée — d'où les deux temps.
    const currentY = smoother.scrollTop();
    const rawY = smoother.offset(target, "top top");
    const offset = headerOffset(currentY, rawY, isLoad);

    // Clamp comme le fait `smoother.scrollTo()` : `offset()` peut renvoyer une
    // position au-delà de la fin du document (ancre sur une section finale plus
    // courte que le viewport) et le setter `scrollTop` ne borne que le bas.
    const y = gsap.utils.clamp(
      0,
      ScrollTrigger.maxScroll(window),
      offset ? smoother.offset(target, "top " + offset + "px") : rawY
    );

    if (!animate) {
      smoother.scrollTop(y);
      focusTarget(target);
      return;
    }

    gsap.to(smoother, {
      scrollTop: y,
      duration: DURATION,
      ease: EASE,
      overwrite: "auto",
      onComplete: () => focusTarget(target),
    });
  }

  function resolveTarget(hash) {
    if (!hash || hash.length < 2) {
      return null;
    }

    try {
      return document.querySelector(hash);
    } catch (error) {
      return null; // hash non valide comme sélecteur CSS
    }
  }

  document.querySelectorAll('a[href*="#"]').forEach((link) => {
    link.addEventListener("click", function (event) {
      // GSAP absent : on ne touche à rien, le navigateur fait son saut natif.
      if (!gsapReady()) {
        return;
      }

      // Clic modifié (nouvel onglet, nouvelle fenêtre, clic milieu) ou déjà
      // traité par un autre handler : comportement natif.
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      // Uniquement les ancres de LA page courante : un lien vers
      // /a-propos#parcours depuis une autre page doit naviguer normalement.
      if (this.host !== window.location.host) {
        return;
      }

      if (
        this.pathname.replace(/\/$/, "") !==
        window.location.pathname.replace(/\/$/, "")
      ) {
        return;
      }

      const target = resolveTarget(this.hash);

      if (!target) {
        return;
      }

      event.preventDefault();
      scrollTo(target, true);

      // `replaceState` et non `pushState` : comme on annule le saut natif, une
      // entrée d'historique par ancre obligerait à autant de « Précédent » pour
      // quitter la page, sans que la vue ne bouge (le navigateur n'a jamais
      // scrollé lui-même). L'URL reste partageable, « Précédent » sort de la page.
      if (window.history && history.replaceState) {
        history.replaceState(null, "", this.hash);
      }
    });
  });

  /**
   * Arrivée sur la page avec un hash (/a-propos#parcours) : on attend `load`
   * pour que les images et les webfonts aient fini de décaler la mise en page,
   * on rafraîchit ScrollTrigger, puis on positionne directement — pas
   * d'animation, la page vient de s'afficher.
   */
  window.addEventListener("load", function () {
    if (!gsapReady() || userScrolled) {
      return;
    }

    const target = resolveTarget(window.location.hash);

    if (!target) {
      return;
    }

    requestAnimationFrame(function () {
      if (userScrolled) {
        return;
      }

      ScrollTrigger.refresh();
      scrollTo(target, false);
    });
  });
}
